<template>
    <component
        :is="is_simple ? 'div' : 'm-pageframe'"
        :title="is_simple ? null : '{organization-label}: Select Lightbox Template'"
    >
        <v-container
            fluid
            class="px-md-8"
        >
            <v-row
                v-if="!is_simple"
                justify="space-between"
                align="center"
                class="my-2"
                dense
            >
                <v-col cols="auto">
                    <link-back
                        text="Cancel"
                        :fallback_route="back_route"
                    />
                </v-col>

                <v-col cols="auto">
                    <v-btn
                        v-if="!editing_existing_lightbox"
                        color="primary"
                        text
                        class="px-md-0"
                        @click="proceedWithoutTemplate"
                    >Skip This Step</v-btn>

                    <v-btn
                        v-if="last_selected_template"
                        color="primary"
                        @click="proceedWithTemplate"
                        class="ml-md-5"
                    >
                        {{ save_cta_text }}
                    </v-btn>
                </v-col>
            </v-row>

            <v-row
                align="start"
                justify="space-between"
                class="mt-0"
            >
                <!-- Header -->
                <v-col
                    v-if="!is_simple"
                    cols="12"
                    class="pb-1"
                >
                    <v-row
                        align="end"
                        justify="space-between"
                    >
                        <v-col
                            cols="12"
                            xl="6"
                        >
                            <h2>{{ page_heading }}</h2>
                            <p class="text-body-2 text--secondary mb-md-4">{{ page_subheading }}</p>
                        </v-col>

                        <!-- Customizations -->
                        <v-col>
                            <v-row
                                justify-xl="end"
                                align="start"
                            >
                                <v-col
                                    v-for="(colorField, color_key) in theme.colors"
                                    :key="color_key"
                                    cols="12"
                                    md="4"
                                >
                                    <m-color-input
                                        v-model="theme.colors[color_key].value"
                                        :label="colorField.label"
                                        storage_key="preferred_lightbox_template_color"
                                        :hide_details="false"
                                        dense
                                        outlined
                                    />
                                </v-col>

                                <v-col cols="12" md="4" lg="5">
                                    <font-selector
                                        ref="fontSelector"
                                        v-model="theme.font_family"
                                        :organization-id="$route.params.organization_id"
                                        label="Font Family"
                                        placeholder="Choose a font for this lightbox"
                                        :hide_details="false"
                                        outlined
                                        dense
                                        clearable
                                        @input="onFontSelected"
                                    />
                                </v-col>

                            </v-row>
                        </v-col>
                    </v-row>
                </v-col>

                <!-- Gallery-->
                <v-col
                    cols="12"
                    class="my-0 pt-0"
                >
                    <div v-if="theme">
                        <m-lightbox-template-gallery
                            :lightbox_params="save_params"
                            :theme="theme"
                            :selected_template_id="last_selected_template?.id"
                            @template-selected="onTemplateSelected"
                            @template-deselected="deselectTemplate"
                        />
                    </div>
                </v-col>
            </v-row>

            <v-row
                v-if="!is_simple"
                justify="end"
                align="center"
                class="py-8"
                dense
            >
                <v-col
                    cols="12"
                    md="auto"
                >
                    <v-btn
                        v-if="!editing_existing_lightbox"
                        color="primary"
                        text
                        @click="proceedWithoutTemplate"
                    >
                        Skip This Step
                    </v-btn>

                    <v-btn
                        v-if="last_selected_template"
                        color="primary"
                        class="ml-2"
                        @click="proceedWithTemplate"
                    >
                        {{ save_cta_text }}
                    </v-btn>
                </v-col>
            </v-row>
        </v-container>
    </component>
</template>

<script>
import _ from 'lodash';
import mLightboxTemplateGallery from '@/components/lightbox/m-lightbox-template-gallery.vue';
import fontLoader from '../../mixins/font-loader.js';
import FontSelector from '../../components/input/font-selector.vue';
import mColorInput from '../../components/input/m-color-input.vue';

export default {
    name: 'LightboxTemplateSelection',

    mixins: [fontLoader],

    components: {
        mLightboxTemplateGallery,
        FontSelector,
        mColorInput,
    },

    props: {
        /** 
         * Flag that indicates whether this is a "simplified" version (just the gallery, no header/theme customization, not wrapped in page frame, etc)
         * Intended for use in other parts of the app where we want to just show the gallery while allowing template selection outside of the actual lightbox flow
         * e.g. allowing users to indicate a preliminary template choice during website setup on the welcome screen
         */
        is_simple: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            last_selected_template: null,
            save_params: null,
            default_stub: null,
            organization_id: null, // set in mounted()
            theme: {
                font_family: window.sessionStorage.getItem('preferred_lightbox_font_family') || 'Arial, sans-serif', // since existing lightboxes can have multiple fonts throughout, default to a safe one
                colors: {
                    primary: {
                        label: 'Primary Color',
                        value: window.sessionStorage.getItem('preferred_lightbox_template_color') || '#007bff',
                    },
                },
                header: {
                    type: 'text',
                    text: '',
                    image: null
                }
            }
        };
    },

    computed: {
        editing_existing_lightbox() {
            return Boolean(this.$route.query.lightbox_id);
        },

        page_heading() {
            if (this.editing_existing_lightbox) {
                return 'Switch to Template';
            }
            return 'Select a Template (Optional)';
        },

        page_subheading() {
            if (this.editing_existing_lightbox) {
                return `Select a template to apply to your lightbox. You'll be able to customize the imagery, styles, and content further on the next page.`;
            }
            return `For a quicker setup, choose from one of our pre-made templates with your own basic styles applied. You'll be able to customize the imagery, styles, and content of the lightbox on the next page.`;
        },

        back_route() {
            if (this.editing_existing_lightbox) {
                return {
                    name: 'lightbox-edit',
                    params: {
                        organization_id: this.organization_id,
                        lightbox_id: this.$route.query.lightbox_id,
                    }
                };
            }
            return {
                name: 'lightbox-search-settings',
                params: {
                    organization_id: this.organization_id,
                }
            };
        },

        save_cta_text() {
            if (this.editing_existing_lightbox) {
                return 'Apply Selection';
            }
            return 'Continue With Selection';
        },
    },

    methods: {
        onFontSelected() {
            const selectedFont = this.theme.font_family;
            if (selectedFont) {
                window.sessionStorage.setItem('preferred_lightbox_font_family', selectedFont);
            }
        },

        onTemplateSelected(template) {
            this.last_selected_template = template;
            this.$emit('template-selected', template);
        },

        deselectTemplate() {
            this.last_selected_template = null;
            this.$emit('template-deselected');
        },

        interpolateTheme(styleString) {
            if (typeof styleString !== 'string') return styleString;

            let interpolated = styleString;

            // placeholders were used to interpolate theming. substitute here
            interpolated = interpolated.replace(/{primary_color}/g, this.theme.colors.primary?.value || '#007bff');
            interpolated = interpolated.replace(/{font_family}/g, this.theme.font_family || 'Arial, sans-serif');

            return interpolated;
        },

        applyTemplateToSaveParams(template) {
            if (!template.template_overrides) return;

            // avoid overriding configuration - only styles
            // unfortunately, we have to do this manually due to the flat structure of save_params
            // this should be improved in the future
            const fields_to_copy = [
                'agreement',
                'background_blur',
                'banner_text',
                'birthday',
                'close_icon',
                'container',
                'container_background',
                'container_content',
                'email_address',
                'first_name',
                'header_background',
                'last_name',
                'line_1',
                'line_2',
                'line_3',
                'logo_image',
                'phone_number',
                'send_button',
                'send_button_text',
                'thank_you_button',
                'thank_you_button_text',
                'thank_you_line_1',
                'thank_you_line_2',
                'use_logo',
            ];
            const filtered_overrides = _.pick(template.template_overrides, fields_to_copy);

            // if filtered_overrides was missing any of the fields_to_copy, pull from the default_stub
            for (const field of fields_to_copy) {
                if (this.save_params[field] != null && filtered_overrides[field] == null) {
                    filtered_overrides[field] = this.default_stub[field];
                }
            }

            for (const [field_name, overrides] of Object.entries(filtered_overrides)) {
                // if not object, just interpolate directly
                if (["string", "number", "boolean"].includes(typeof overrides)) {
                    this.save_params[field_name] = this.interpolateTheme(overrides);
                    continue;
                }

                // otherwise, recursively interpolate each property
                if (this.save_params[field_name] != null) {
                    for (const [prop, value] of Object.entries(overrides)) {
                        if (this.save_params?.[field_name]?.[prop] !== undefined) {
                            this.save_params[field_name] = {
                                ...this.save_params[field_name],
                                [prop]: this.interpolateTheme(value),
                            };
                        }
                    }
                }
            }
        },

        async doSaveLightboxSettings() {
            return new Promise((resolve, reject) => {
                this.$rc.rest({
                    spinner: false,
                    endpoint: 'PUT: /api/v1/lightbox/settings',

                    params: this.save_params,

                    on_success: (data) => {
                        this.save_params = data;
                        resolve(data);
                    },

                    on_error: (err) => {
                        this.$rc.alert('Unable to save lightbox settings');
                        reject(err);
                    },
                });
            });
        },

        async doSaveAndProceed(template = null) {
            if (template) {
                this.applyTemplateToSaveParams(template);
            }

            await this.doSaveLightboxSettings();
            this.sendToEditPage();
        },

        async proceedWithoutTemplate() {
            await this.doSaveAndProceed();
        },

        async proceedWithTemplate() {
            if (this.editing_existing_lightbox) {
                this.$rc.confirm(
                    'Confirm',
                    'Are you sure you want to apply this template? This will override your existing content and style. Your other settings will remain intact.',
                    [
                        { 'text': 'Cancel', 'value': 'cancel' },
                        { 'text': 'Confirm', 'value': 'confirm' },
                    ],
                    (response) => {
                        if (response == 'confirm') {
                            this.doSaveAndProceed(this.last_selected_template);
                        } else {
                            return;
                        }
                    }
                );
            } else {
                await this.doSaveAndProceed(this.last_selected_template);
            }
        },

        sendToEditPage() {
            this.$router.push({
                'name': 'lightbox-edit',
                'params': {
                    'organization_id': this.organization_id,
                    'lightbox_id': this.save_params.lightbox_id,
                }
            });
        },

        createLightboxStub() {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/lightbox/settings/create-stub',

                'params': {
                    'organization_id': this.organization_id,
                    'label': null,
                },

                'on_success': (data) => {
                    if (!this.editing_existing_lightbox) {
                        this.save_params = data;
                    }
                    this.default_stub = data;
                },

                'on_error': (err) => {
                    this.$rc.alert('Unable to create new lightbox');
                }
            });
        },

        loadExistingLightbox() {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'GET: /api/v1/lightbox/settings/:organization_id/:lightbox_id',

                'params': {
                    'organization_id': this.organization_id,
                    'lightbox_id': this.$route.query.lightbox_id,
                },

                'on_success': (data) => {
                    this.save_params = data;
                },

                'on_error': 'Unable to load lightbox settings',
            });
        },
    },

    mounted() {
        this.organization_id = this.$rc.$state.organization_id || this.$route.params.organization_id;

        if (!this.is_simple) {
            this.loadFontFamilies(this.organization_id);
        }

        if (this.$route.query.lightbox_id) {
            this.loadExistingLightbox();
        }
        this.createLightboxStub();
    },
};
</script>

<style scoped lang="scss">
.help-container {
    max-width: 250px;
}
</style>